import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  InjectionToken,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core'
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, provideMomentDateAdapter } from '@angular/material-moment-adapter'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { MatIconRegistry } from '@angular/material/icon'
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading } from '@angular/router'
import {
  artificialDelayInterceptor,
  errorInterceptorFn,
  isCustomerFacingStage,
  provideSharedService,
  provideStageTitle,
  provideWhatsappIcon,
  provideZahlenfelderOhneMausrad,
} from '@saniup-webapps/shared'
import { env } from '../environments/environment'
import { EnvironmentModel } from '../environments/environment.model'
import { appRoutes } from './app.routes'
import { IdlePreload } from './util/preload.strategy'

import { registerLocaleData } from '@angular/common'
import localeDe from '@angular/common/locales/de'
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin'
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin'
import { provideStore } from '@ngxs/store'
import 'moment/locale/de'
import { auditInterceptorFn } from './interceptors/audit.interceptor'
import { errorMsgInterceptor } from './interceptors/error-msg.interceptor'
import { AppState } from './state/app.state'
import { ProduktCartState } from './state/produkt-cart.state'

registerLocaleData(localeDe)

export const ENV_TOKEN = new InjectionToken<EnvironmentModel>('app-env')

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENV_TOKEN, useValue: env() },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass('material-symbols-outlined')
    }),
    provideZonelessChangeDetection(),
    // Reicht unbehandelte Promise-Fehler (z. B. ein fehlender Lazy-Chunk nach einem Deploy) an den
    // GlobalErrorHandlerService weiter, der dann neu laedt. Zoneless tut das sonst niemand.
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withPreloading(IdlePreload),
      withComponentInputBinding(),
    ),
    provideHttpClient(
      withInterceptors([errorMsgInterceptor, errorInterceptorFn(), auditInterceptorFn(), artificialDelayInterceptor]),
      withFetch(),
    ),
    provideMomentDateAdapter({
      parse: {
        dateInput: ['l', 'LL'],
      },
      display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
      },
    }),
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    provideZahlenfelderOhneMausrad(),
    provideStageTitle(),
    provideSharedService({
      stage: env().stage,
      // Patient:innen werden gesiezt (geteilte Texte wie der Fehlerzustand richten sich danach).
      anrede: 'sie',
    }),
    provideWhatsappIcon(),
    provideStore(
      [ProduktCartState, AppState],
      {
        developmentMode: !isCustomerFacingStage(env().stage),
      },
      withNgxsStoragePlugin({ keys: [ProduktCartState, AppState] }),
      withNgxsReduxDevtoolsPlugin({ disabled: isCustomerFacingStage(env().stage) }),
    ),
  ],
}
